import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const SeasonalTimeline = ({ seasonalData }) => {
  const [selectedMonth, setSelectedMonth] = useState(new Date()?.getMonth());

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const currentSeasonData = seasonalData?.[selectedMonth];

  return (
    <div className="bg-card rounded-lg p-6 shadow-conservation">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-xl font-semibold text-foreground">Seasonal Wildlife Calendar</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Discover the best times to observe different species
          </p>
        </div>
        <Icon name="Calendar" size={24} color="var(--color-primary)" />
      </div>
      {/* Month Selector */}
      <div className="mb-6 overflow-x-auto">
        <div className="flex gap-2 pb-2">
          {months?.map((month, index) => (
            <button
              key={index}
              onClick={() => setSelectedMonth(index)}
              className={`flex-shrink-0 px-4 py-2 rounded-lg text-sm font-medium animation-organic ${
                selectedMonth === index
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
            >
              {month}
            </button>
          ))}
        </div>
      </div>
      {/* Season Info */}
      <div className="grid md:grid-cols-3 gap-4 mb-6">
        <div className="bg-muted p-4 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="Cloud" size={20} color="var(--color-info)" />
            <span className="text-sm font-semibold text-foreground">Weather</span>
          </div>
          <p className="text-sm text-muted-foreground">{currentSeasonData?.weather}</p>
        </div>

        <div className="bg-muted p-4 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="Thermometer" size={20} color="var(--color-warning)" />
            <span className="text-sm font-semibold text-foreground">Temperature</span>
          </div>
          <p className="text-sm text-muted-foreground">{currentSeasonData?.temperature}</p>
        </div>

        <div className="bg-muted p-4 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="Users" size={20} color="var(--color-secondary)" />
            <span className="text-sm font-semibold text-foreground">Visitor Level</span>
          </div>
          <p className="text-sm text-muted-foreground">{currentSeasonData?.visitorLevel}</p>
        </div>
      </div>
      {/* Wildlife Activity */}
      <div className="space-y-4">
        <h4 className="text-sm font-semibold text-foreground">Active Wildlife This Month</h4>
        
        <div className="space-y-3">
          {currentSeasonData?.activeSpecies?.map((species, index) => (
            <div key={index} className="flex items-start gap-3 p-3 bg-muted rounded-lg">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                species?.activityLevel === 'High' ? 'bg-success/20' :
                species?.activityLevel === 'Medium'? 'bg-warning/20' : 'bg-info/20'
              }`}>
                <Icon 
                  name={species?.icon} 
                  size={20} 
                  color={
                    species?.activityLevel === 'High' ? 'var(--color-success)' :
                    species?.activityLevel === 'Medium' ? 'var(--color-warning)' :
                    'var(--color-info)'
                  }
                />
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1">
                  <h5 className="text-sm font-semibold text-foreground">{species?.name}</h5>
                  <span className={`text-xs px-2 py-0.5 rounded ${
                    species?.activityLevel === 'High' ? 'bg-success/20 text-success' :
                    species?.activityLevel === 'Medium'? 'bg-warning/20 text-warning' : 'bg-info/20 text-info'
                  }`}>
                    {species?.activityLevel} Activity
                  </span>
                </div>
                <p className="text-xs text-muted-foreground mb-2">{species?.behavior}</p>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Icon name="MapPin" size={12} />
                  <span>Best spotted in: {species?.location}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Special Events */}
      {currentSeasonData?.specialEvents && currentSeasonData?.specialEvents?.length > 0 && (
        <div className="mt-6 p-4 bg-accent/10 border border-accent/20 rounded-lg">
          <div className="flex items-start gap-3">
            <Icon name="Star" size={20} color="var(--color-accent)" className="flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="text-sm font-semibold text-foreground mb-2">Special Events This Month</h4>
              <ul className="space-y-1">
                {currentSeasonData?.specialEvents?.map((event, index) => (
                  <li key={index} className="text-sm text-muted-foreground">• {event}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}
      {/* Action Button */}
      <div className="mt-6">
        <Button variant="default" iconName="Calendar" iconPosition="left" fullWidth>
          Plan Visit for {months?.[selectedMonth]}
        </Button>
      </div>
    </div>
  );
};

export default SeasonalTimeline;